import React, { useState } from 'react';

/**
 * Reusable Card Component for Admin Dashboard
 * Provides consistent styling for all card-based sections
 */
function Card({ 
  children, 
  title, 
  icon,
  headerAction,
  className = '',
  noPadding = false,
  variant = 'default', // 'default', 'success', 'warning', 'error', 'info'
  collapsible = false,
  defaultCollapsed = false,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  
  // Variant-based border colors
  const variantStyles = {
    default: 'border-gray-200',
    success: 'border-green-300 bg-green-50/50',
    warning: 'border-yellow-300 bg-yellow-50/50',
    error: 'border-red-300 bg-red-50/50',
    info: 'border-blue-300 bg-blue-50/50',
  };

  return (
    <div 
      className={`
        bg-white rounded-xl border shadow-sm 
        ${variantStyles[variant] || variantStyles.default}
        ${className}
      `}
    >
      {/* Card Header */}
      {title && (
        <div 
          className={`flex items-center justify-between px-4 py-3 ${!isCollapsed ? 'border-b border-gray-100' : ''} ${collapsible ? 'cursor-pointer hover:bg-gray-50 theme-transition' : ''}`}
          onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
        >
          <div className="flex items-center space-x-2">
            {icon && <span className="text-lg">{icon}</span>}
            <h3 className="font-semibold text-gray-800">{title}</h3>
            {collapsible && (
              <span className={`text-gray-400 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}>
                ▼
              </span>
            )}
          </div>
          {headerAction && (
            <div className="text-sm" onClick={(e) => e.stopPropagation()}>
              {headerAction}
            </div>
          )}
        </div>
      )}
      
      {/* Card Content */}
      {!isCollapsed && (
        <div className={noPadding ? '' : 'p-4'}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Card Section - For grouping content within a card
 */
function CardSection({ title, children, className = '' }) {
  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-gray-600 mb-2">{title}</h4>
      )}
      {children}
    </div>
  );
}

/**
 * Stat Card - Compact card showing a single statistic
 */
function StatCard({ 
  label, 
  value, 
  icon, 
  trend, // 'up', 'down', 'neutral'
  color = 'gray', // 'green', 'blue', 'yellow', 'red', 'gray'
  className = '' 
}) {
  const colorStyles = {
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
    purple: 'bg-purple-100 text-purple-800',
  };

  const trendIcons = {
    up: '↑',
    down: '↓',
    neutral: '→',
  };

  return (
    <div className={`rounded-lg p-3 ${colorStyles[color]} ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm opacity-75">{label}</span>
        {icon && <span>{icon}</span>}
      </div>
      <div className="flex items-baseline space-x-2">
        <span className="text-2xl font-bold">{value}</span>
        {trend && (
          <span className="text-sm opacity-75">{trendIcons[trend]}</span>
        )}
      </div>
    </div>
  );
}

Card.Section = CardSection;
Card.Stat = StatCard;

export default Card;
